//TODO refactoring 'eventHandlers' and logic of dragging
import { DivIcon, LatLngExpression, PointExpression } from "leaflet";
import { Fragment, useCallback, useEffect, useState } from "react";
import { renderToString } from "react-dom/server";
import { CircleMarker, Marker, Polygon, useMap } from "react-leaflet";
import { useDispatch, useSelector } from "react-redux";
import { toastr } from "react-redux-toastr";
import { useParams } from "react-router-dom";

import {
  LayerAlias,
  LAYERS,
  MapModes,
  PointClassificationsNames,
  UnitTypes
} from "constant";
import { useMapContext } from "context/MapContextProvider";
import features from "features";
import useShiftMap from "hooks/useShiftMap";
import { RootStateInterface } from "reducer";
import { MapLayerInterface } from "scenes/LayersManager/interfaces";
import { MarkerInterface } from "scenes/MapPage/ducks";
import { MapStateInterface } from "../../ducks";

import IconButton from "components/buttons/IconButton/IconButton";
import MapMarkerContextMenu from "../MapMarkerContextMenu/MapMarkerContextMenu";
import MapMarkerIcon from "../MapMarkerIcon/MapMarkerIcon";
import MapSensorFusionMarkerIcon from "../MapSensorFusionMarkerIcon/MapSensorFusionMarkerIcon";

import CancelIcon from "assets/icons/cancel.svg";
import CheckIcon from "assets/icons/check.svg";
import MoveIcon from "assets/icons/move-white.svg";

const ACTIVE_MARKER_COLOR = "#09090B";

interface MapMarkersInterface {
  markers: MarkerInterface[];
  activeMarkerId: string;
  opacityByLayerList: MapLayerInterface[];
  isClustering?: boolean;
}

const MapMarkers = ({
  markers,
  activeMarkerId,
  opacityByLayerList,
  isClustering
}: MapMarkersInterface) => {
  const dispatch = useDispatch();
  const map = useMap();
  const { id: projectId } = useParams();
  const { shiftMapPointDetails } = useShiftMap();

  const { mapMode } = useSelector<RootStateInterface, MapStateInterface>(
    (state) => state.map
  );

  const [draggableId, setDraggableId] = useState(null);
  const [viewBounds, setViewBounds] = useState(map.getBounds());

  const {
    openContextMenu,
    closeContextMenu,
    setPointCoordinates,
    pointCoordinates
  } = useMapContext();

  const clearDraggableData = useCallback(() => {
    setDraggableId(null);
    setPointCoordinates(null);
  }, [setPointCoordinates]);

  const onCancelDragging = useCallback(() => {
    clearDraggableData();
  }, [clearDraggableData]);

  const onAcceptDragging = useCallback(() => {
    let markerData = markers.find((marker) => marker.id === draggableId);

    const updatedMarkerData = {
      ...markerData,
      geo_data: {
        ...markerData.geo_data,
        coordinates: [pointCoordinates.lat, pointCoordinates.lng]
      }
    };

    dispatch(
      features.unitsManager.actions.updateUnitRequest({
        params: {
          projectId,
          unitId: draggableId,
          type: UnitTypes.POINT,
          alias: markerData.layerAlias
        },
        fields: updatedMarkerData,
        onSuccess: () => {
          clearDraggableData();
          toastr.success(`Done`, `Point updated`);
        }
      })
    );
  }, [
    clearDraggableData,
    dispatch,
    draggableId,
    markers,
    projectId,
    pointCoordinates
  ]);

  const getMarkerIcon = (marker, number) => {
    const iconSize: PointExpression = [2, 17];
    const iconAnchor: PointExpression = [1, 18];
    const markerColor =
      marker?.additional?.classification?.color ??
      LAYERS[marker.layerAlias].color;

    const markerName = `${
      PointClassificationsNames[marker.additional.classification.alias]
    }_${number}`;
    // marker.layerAlias === LayerAlias.MULTISPECTRAL ? "" : marker?.name;

    return new DivIcon({
      html: renderToString(
        <div>
          {draggableId === marker.id && (
            <>
              <img
                className="marker-move-icon"
                src={MoveIcon}
                alt=""
                style={{
                  top: 4,
                  left: -15
                }}
              />
              <div className="marker-options">
                <div id="marker-check" className="marker-button">
                  <IconButton icon={CheckIcon} alt={""}></IconButton>
                </div>
                <div id="marker-cancel" className="marker-button">
                  <IconButton icon={CancelIcon} alt={""}></IconButton>
                </div>
              </div>
            </>
          )}
          <div style={{ opacity: draggableId === marker.id ? 0.7 : 1 }}>
            {marker.layerAlias === LayerAlias.SENSOR_FUSION ? (
              <MapSensorFusionMarkerIcon
                color={markerColor}
                sensorFusion={marker.additional}
                probability={marker.additional.probability}
                markerName={markerName}
                isActive={activeMarkerId === marker.id}
              />
            ) : (
              <MapMarkerIcon
                color={markerColor}
                probability={
                  marker.layerAlias === LayerAlias.MANUAL
                    ? "manual"
                    : marker.additional.probability
                }
                markerName={markerName}
                isActive={activeMarkerId === marker.id}
              />
            )}
          </div>
        </div>
      ),
      className: "marker-reset",
      iconSize,
      iconAnchor
    });
  };

  const onMarkerClick = useCallback(
    (marker) => () => {
      dispatch(
        features.unitsManager.actions.fetchUnitRequest({
          params: { projectId, alias: marker.layerAlias, id: marker.id }
        })
      );

      shiftMapPointDetails(marker.geo_data.coordinates);
    },
    [dispatch, projectId, shiftMapPointDetails]
  );

  const onMoveMarker = useCallback(
    (marker) => () => {
      setDraggableId(marker.id);
      setPointCoordinates({
        lat: marker.geo_data.coordinates[0],
        lng: marker.geo_data.coordinates[1]
      });
      closeContextMenu();
    },
    [closeContextMenu, setPointCoordinates]
  );

  const isVisible = useCallback(
    (coordinates) => viewBounds && viewBounds.intersects([coordinates]),
    [viewBounds]
  );

  useEffect(() => {
    const handleMove = () => {
      setViewBounds(map.getBounds());
    };

    map.on("moveend", handleMove);
    return () => {
      map.off("moveend", handleMove);
    };
  }, [map]);

  if (markers.length === 0) {
    return null;
  }

  function createSquare(center, size) {
    const [lat, lng] = center;
    const halfSize = size / 2;

    const lngCorrection = halfSize / Math.cos((lat * Math.PI) / 180);

    return [
      [lat, lng],
      [lat, lng + lngCorrection],
      [lat - halfSize, lng + lngCorrection],
      [lat - halfSize, lng]
    ];
  }

  return (
    <>
      {markers.map((marker: MarkerInterface, index: number) => (
        <Fragment key={`marker-${index}`}>
          {isVisible(
            draggableId === marker.id
              ? pointCoordinates
              : (marker.geo_data.coordinates as LatLngExpression)
          ) &&
            !isClustering && (
              <Marker
                position={
                  draggableId === marker.id
                    ? pointCoordinates
                    : (marker.geo_data.coordinates as LatLngExpression)
                }
                icon={getMarkerIcon(marker, index + 1)}
                opacity={
                  opacityByLayerList.find(
                    (layer) => layer.alias === marker.layerAlias
                  )?.opacity / 100
                }
                bubblingMouseEvents={true}
                draggable={draggableId === marker.id}
                eventHandlers={
                  draggableId
                    ? {
                        click: (e) => {
                          const target = e.originalEvent.target;
                          if (target instanceof HTMLElement) {
                            const confirmButton =
                              target.closest("#marker-check");
                            const cancelButton =
                              target.closest("#marker-cancel");

                            if (confirmButton) {
                              onAcceptDragging();
                            } else if (cancelButton) {
                              onCancelDragging();
                            }
                          }
                        },
                        dragend: (e) => {
                          const { lat, lng } = e.target.getLatLng();
                          setPointCoordinates({ lat, lng });
                        }
                      }
                    : {
                        click: onMarkerClick(marker),
                        contextmenu: (e) => {
                          const containerPoint = e.containerPoint;

                          mapMode === MapModes.DEFAULT &&
                            setTimeout(() => {
                              //A timeout is needed to show the current data of the polygon we click on. Due to the click event handler that is running in parallel in the MapEventHandler component, we cannot do this as usual.
                              openContextMenu({
                                position: {
                                  x: containerPoint.x,
                                  y: containerPoint.y
                                },
                                customMenu: (
                                  <MapMarkerContextMenu
                                    marker={marker}
                                    onMove={onMoveMarker(marker)}
                                  />
                                )
                              });
                            }, 0);
                        }
                      }
                }
              />
            )}
          {isClustering ? (
            <CircleMarker
              key={`marker-${marker.id}-${
                activeMarkerId === marker.id ? "active" : ""
              }`}
              color={
                activeMarkerId === marker.id
                  ? ACTIVE_MARKER_COLOR
                  : marker?.additional?.classification?.color ??
                    LAYERS[marker.layerAlias].color
              }
              center={marker.geo_data.coordinates as LatLngExpression}
              radius={2}
              eventHandlers={
                draggableId
                  ? {
                      click: (e) => {
                        const target = e.originalEvent.target;
                        if (target instanceof HTMLElement) {
                          const confirmButton = target.closest("#marker-check");
                          const cancelButton = target.closest("#marker-cancel");

                          if (confirmButton) {
                            onAcceptDragging();
                          } else if (cancelButton) {
                            onCancelDragging();
                          }
                        }
                      },
                      dragend: (e) => {
                        const { lat, lng } = e.target.getLatLng();
                        setPointCoordinates({ lat, lng });
                      }
                    }
                  : {
                      click: onMarkerClick(marker),
                      contextmenu: (e) => {
                        const containerPoint = e.containerPoint;

                        mapMode === MapModes.DEFAULT &&
                          setTimeout(() => {
                            //A timeout is needed to show the current data of the polygon we click on. Due to the click event handler that is running in parallel in the MapEventHandler component, we cannot do this as usual.
                            openContextMenu({
                              position: {
                                x: containerPoint.x,
                                y: containerPoint.y
                              },
                              customMenu: (
                                <MapMarkerContextMenu
                                  marker={marker}
                                  onMove={onMoveMarker(marker)}
                                />
                              )
                            });
                          }, 0);
                      }
                    }
              }
            />
          ) : (
            <Polygon
              key={`marker-${marker.id}-${
                activeMarkerId === marker.id ? "active" : ""
              }`}
              positions={createSquare(marker.geo_data.coordinates, 0.000005)}
              color={
                activeMarkerId === marker.id
                  ? ACTIVE_MARKER_COLOR
                  : marker?.additional?.classification?.color ??
                    LAYERS[marker.layerAlias].color
              }
              eventHandlers={
                draggableId
                  ? {
                      click: (e) => {
                        const target = e.originalEvent.target;
                        if (target instanceof HTMLElement) {
                          const confirmButton = target.closest("#marker-check");
                          const cancelButton = target.closest("#marker-cancel");

                          if (confirmButton) {
                            onAcceptDragging();
                          } else if (cancelButton) {
                            onCancelDragging();
                          }
                        }
                      },
                      dragend: (e) => {
                        const { lat, lng } = e.target.getLatLng();
                        setPointCoordinates({ lat, lng });
                      }
                    }
                  : {
                      click: onMarkerClick(marker),
                      contextmenu: (e) => {
                        const containerPoint = e.containerPoint;

                        mapMode === MapModes.DEFAULT &&
                          setTimeout(() => {
                            //A timeout is needed to show the current data of the polygon we click on. Due to the click event handler that is running in parallel in the MapEventHandler component, we cannot do this as usual.
                            openContextMenu({
                              position: {
                                x: containerPoint.x,
                                y: containerPoint.y
                              },
                              customMenu: (
                                <MapMarkerContextMenu
                                  marker={marker}
                                  onMove={onMoveMarker(marker)}
                                />
                              )
                            });
                          }, 0);
                      }
                    }
              }
            />
          )}
        </Fragment>
      ))}
    </>
  );
};
export default MapMarkers;
